import { TrashIcon } from '@heroicons/react/24/outline'
import { useEffect, useRef, useState } from 'react'

import { ActionModal } from '@ui/components/core'
import { showToast } from '@ui/components/elements/ToastManager'
import { Member } from '@ui/interfaces'
import axios from '@ui/lib/api/axiosHelper'
import { InviteMemberModal } from '@ui/pages/manage/components/InviteMemberModal'
import { useAuth } from '@ui/providers/AuthProvider'
import dayjs from 'dayjs'

type MemberListProps = {
  modalState: boolean
  setModalState: (modalState: boolean) => void
}

export const Members = ({ modalState, setModalState }: MemberListProps) => {
  const auth = useAuth()
  const { currentOrganization } = auth
  const orgId = currentOrganization!.id

  const [members, setMembers] = useState<Member[] | null>(null)
  const [deleteConfirmationModal, setDeleteConfirmationModal] = useState(false)
  const [selectedMember, setSelectedMember] = useState<Member | null>(null)

  const setModalClosed = () => {
    setModalState(false)
  }

  const effectFired = useRef(false)
  useEffect(() => {
    if (!effectFired.current) {
      effectFired.current = true
      fetchMembers()
    }
  }, [])

  const fetchMembers = () => {
    const configuration = {
      url: `/v1/organizations/${orgId}/members`,
    }

    axios
      .get(configuration.url)
      .then((response: { data: Member[] }) => {
        setMembers(response.data)
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .catch((err: any) => {
        // if we get a 401, redirect to login
        if (err.response.status === 401) {
          window.location.href = '/login'
        }
        console.log('Failed to get members from organization', orgId, ': ', err)
        showToast.error('Failed to retrieve members')
      })
  }

  const removeMember = (memberId: number) => {
    const configuration = {
      url: `/v1/organizations/${orgId}/members/${memberId}`,
    }
    axios
      .delete(configuration.url)
      .then(() => {
        showToast.success('Member has been removed')

        fetchMembers()
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .catch((err: any) => {
        console.log('Failed to delete member ', memberId, ' from org ', orgId, ': ', err)
        showToast.error('Failed to remove member')
      })
  }

  return (
    <>
      <ActionModal
        open={deleteConfirmationModal}
        title="Delete Member"
        message="Are you sure you want to remove this member?"
        handleAction={() => {
          removeMember(selectedMember?.id || 0)
          setDeleteConfirmationModal(false)
        }}
        actionIdentifier="Remove"
        dismissAction={() => {
          setDeleteConfirmationModal(false)
        }}
        dismissIdentifier="Cancel"
      />
      <InviteMemberModal
        fetchMembers={fetchMembers}
        modalState={modalState}
        setModalClosed={setModalClosed}
        orgId={orgId}
      />
      <div className="mt-5 flow-root h-full">
        <div className="-my-2 h-full w-full overflow-x-auto rounded-md">
          <div className="inline-block min-w-full align-middle">
            <div className="sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="sticky top-0 z-10 border-b border-gray-300 bg-white py-3.5 pl-4 pr-3 text-left text-sm font-medium text-gray-900 backdrop-blur sm:pl-6">
                      Name
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 z-10 border-b border-gray-300 bg-white py-3.5 pl-4 pr-3 text-left text-sm font-medium text-gray-900 backdrop-blur sm:pl-6">
                      Email
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 z-10 border-b border-gray-300 bg-white py-3.5 pl-4 pr-3 text-left text-sm font-medium text-gray-900 backdrop-blur sm:pl-6">
                      Last Sign In
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 z-10 border-b border-gray-300 bg-white py-3.5 pl-3 pr-4 backdrop-blur sm:pr-0">
                      <span className="sr-only">Remove User</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {members &&
                    members.map((member: Member) => (
                      <tr key={member.email}>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-normal text-gray-900 sm:pl-6">
                          {member.first_name} {member.last_name}
                        </td>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-normal text-gray-900 sm:pl-6">
                          {member.email}
                        </td>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-normal capitalize text-gray-900 sm:pl-6">
                          {member.current_sign_in_at && <p>{dayjs(member.current_sign_in_at).fromNow()}</p>}

                          {!member.current_sign_in_at && <p>Never</p>}
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-sm font-normal sm:pr-0">
                          <a
                            onClick={() => {
                              setSelectedMember(member)
                              setDeleteConfirmationModal(true)
                            }}>
                            <TrashIcon className="h-6 w-6 cursor-pointer stroke-red-800" aria-hidden="true" />
                          </a>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
